import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Palette, Eraser, Download, Loader2, Image, RotateCcw, Check, Copy, Pipette, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useImageUpload } from './chart-converter/useImageUpload';
import { useColorReplace } from './chart-converter/useColorReplace';
import { useRegionErase } from './chart-converter/useRegionErase';

/**
 * ChartConverter - Chart image conversion modal
 * Features: Color change (eyedropper + color replacement) & Region erase (AI inpainting)
 */
const ChartConverter = ({ onClose, onSendToChat }) => {
  const [selectedMode, setSelectedMode] = useState('color');
  const imageRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Hook composition
  const upload = useImageUpload();
  const color = useColorReplace({ imageRef, previewUrl: upload.previewUrl, selectedMode });
  const erase = useRegionErase({ canvasRef, imageRef });

  // Full reset
  const resetAll = () => {
    upload.resetUpload();
    color.resetColor();
    erase.resetErase();
  };

  // Display image URL based on current mode
  const displayImageUrl = selectedMode === 'color'
    ? (color.colorResultUrl || upload.previewUrl)
    : (erase.resultImageUrl || upload.previewUrl);

  const isProcessing = color.isProcessing || erase.isProcessing;

  return (
    <motion.div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-bg-100 rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-4 pb-3 border-b border-bg-300/30 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-text-100">Chart Converter</h2>
            <button onClick={onClose} className="p-2 rounded-lg text-text-400 hover:text-text-200 hover:bg-bg-300/50 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="text-xs text-text-400 dark:text-text-500 bg-bg-200/60 dark:bg-bg-300/30 px-3 py-1.5 rounded-lg border border-bg-300/40 dark:border-bg-300/20">
            ⚠️ This service is currently in an advanced development phase, and some features may not be stable.
          </div>
          <ModeSelector
            selectedMode={selectedMode}
            onSelectColor={async () => {
              if (erase.resultImageUrl) {
                try {
                  const res = await fetch(erase.resultImageUrl);
                  const blob = await res.blob();
                  upload.setPreviewUrl(URL.createObjectURL(blob));
                } catch { upload.setPreviewUrl(erase.resultImageUrl); }
                erase.setResultImageUrl(null);
                erase.setSelectedRegions([]);
              }
              setSelectedMode('color');
            }}
            onSelectErase={async () => {
              if (color.colorResultUrl) {
                upload.setPreviewUrl(color.colorResultUrl);
                color.setColorResultUrl(null);
                color.setColorMappings([]);
                color.setExcludeRegions([]);
                try {
                  const res = await fetch(color.colorResultUrl);
                  const blob = await res.blob();
                  const file = new File([blob], 'color_result.png', { type: 'image/png' });
                  upload.setS3ImageUrl(null);
                  upload.processFile(file);
                } catch (e) { console.error('Re-upload error:', e); }
              }
              setSelectedMode('erase');
            }}
          />
          <p className="text-sm text-text-400">
            {selectedMode === 'color' ? 'Use the eyedropper to pick a color from the chart image, or drag to exclude a region' : 'Select logos, watermarks, or other unwanted areas on the chart and AI will automatically erase them.'}
          </p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Image upload/edit area */}
          <ImageArea
            containerRef={containerRef}
            upload={upload}
            color={color}
            erase={erase}
            imageRef={imageRef}
            canvasRef={canvasRef}
            selectedMode={selectedMode}
            displayImageUrl={displayImageUrl}
            isProcessing={isProcessing}
          />

          {/* Color change mode UI */}
          {selectedMode === 'color' && upload.previewUrl && !color.colorResultUrl && (
            <ColorControls color={color} />
          )}

          {/* Color change result */}
          {selectedMode === 'color' && color.colorResultUrl && (
            <ResultBanner text="Color change complete!" sub="You can download or copy the result image." />
          )}

          {/* Region erase selection list */}
          {selectedMode === 'erase' && erase.selectedRegions.length > 0 && !erase.resultImageUrl && (
            <RegionList regions={erase.selectedRegions} onRemove={erase.removeRegion} onClear={erase.clearAllRegions} />
          )}

          {/* Region erase result */}
          {selectedMode === 'erase' && erase.resultImageUrl && (
            <ResultBanner text="Region erase complete!" sub="You can download the result image or select another region to process again." />
          )}

        </div>

        {/* Footer */}
        <Footer
          selectedMode={selectedMode}
          upload={upload}
          color={color}
          erase={erase}
          resetAll={resetAll}
          isProcessing={isProcessing}
          onSendToChat={onSendToChat}
          onClose={onClose}
        />
      </motion.div>
    </motion.div>
  );
};

export default ChartConverter;


// ===== Sub-components =====

function ModeSelector({ selectedMode, onSelectColor, onSelectErase }) {
  return (
    <div className="flex border-b border-bg-300/30">
      <button
        onClick={onSelectColor}
        className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-all relative ${
          selectedMode === 'color'
            ? 'text-violet-400'
            : 'text-text-400 hover:text-text-200'
        }`}
      >
        <Palette className="w-4 h-4" />
        <span>Color Change</span>
        {selectedMode === 'color' && (
          <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-violet-400 rounded-full" />
        )}
      </button>
      <button
        onClick={onSelectErase}
        className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-all relative ${
          selectedMode === 'erase'
            ? 'text-lime-500'
            : 'text-text-400 hover:text-text-200'
        }`}
      >
        <Eraser className="w-4 h-4" />
        <span>Erase Region</span>
        {selectedMode === 'erase' && (
          <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-lime-500 rounded-full" />
        )}
      </button>
    </div>
  );
}

function ImageArea({ containerRef, upload, color, erase, imageRef, canvasRef, selectedMode, displayImageUrl, isProcessing }) {
  return (
    <div
      ref={containerRef}
      onDrop={upload.handleDrop}
      onDragOver={upload.handleDragOver}
      className={`relative rounded-xl overflow-hidden transition-all ${
        upload.previewUrl
          ? 'bg-[#E8E8ED] dark:bg-[#1a1a2e] border border-bg-300/40 shadow-[inset_0_2px_8px_rgba(0,0,0,0.06)] dark:shadow-[inset_0_2px_8px_rgba(0,0,0,0.2)]'
          : 'border-2 border-dashed border-bg-300/50 hover:border-violet-500/50 hover:bg-violet-500/5 cursor-pointer p-8'
      }`}
      onClick={() => !upload.previewUrl && upload.fileInputRef.current?.click()}
    >
      <input ref={upload.fileInputRef} type="file" accept="image/*" onChange={upload.handleFileSelect} className="hidden" />

      {upload.previewUrl ? (
        <div className="relative">
          <img
            ref={imageRef}
            src={displayImageUrl}
            alt="Chart image"
            className={`w-full max-h-[32rem] object-contain ${
              selectedMode === 'color' && color.isEyedropping && !color.colorResultUrl ? 'cursor-crosshair' : ''
            }`}
            onLoad={erase.drawRegions}
            onClick={color.handleEyedropClick}
            onMouseMove={color.handleEyedropMove}
            onMouseLeave={color.handleEyedropLeave}
          />

          {/* Eyedropper color preview */}
          {color.eyedropPreview && (
            <div
              className="absolute pointer-events-none z-50 flex items-center gap-2 px-2.5 py-1.5 bg-gray-900/90 text-white text-xs rounded-lg shadow-xl backdrop-blur-sm"
              style={{ left: color.eyedropPreview.x + 16, top: color.eyedropPreview.y - 40 }}
            >
              <div className="w-5 h-5 rounded border border-white/40 shrink-0" style={{ backgroundColor: color.eyedropPreview.color }} />
              <span className="font-mono">{color.eyedropPreview.color}</span>
            </div>
          )}

          {/* Copy button - region erase */}
          {selectedMode === 'erase' && erase.resultImageUrl && (
            <button onClick={erase.handleCopyImage} className="absolute top-3 right-3 p-2.5 bg-white/90 hover:bg-white text-gray-700 hover:text-gray-900 rounded-full shadow-lg backdrop-blur-sm border border-gray-200/50 transition-all hover:scale-105 active:scale-95" title="Copy image">
              <Copy className="w-4 h-4" />
            </button>
          )}

          {/* Copy button - color change */}
          {selectedMode === 'color' && color.colorResultUrl && (
            <button onClick={color.handleCopyColorResult} className="absolute top-3 right-3 p-2.5 bg-white/90 hover:bg-white text-gray-700 hover:text-gray-900 rounded-full shadow-lg backdrop-blur-sm border border-gray-200/50 transition-all hover:scale-105 active:scale-95" title="Copy image">
              <Copy className="w-4 h-4" />
            </button>
          )}

          {/* Eyedropper active indicator */}
          <FadeBadge show={selectedMode === 'color' && color.isEyedropping && !color.colorResultUrl} color="violet" icon={<Pipette className="w-4 h-4" />} text="Click a color in the image" />

          {/* Exclude region mode indicator */}
          <FadeBadge show={selectedMode === 'color' && color.excludeMode && !color.colorResultUrl} color="blue" icon={<Eraser className="w-4 h-4" />} text="Drag to select a region to exclude" />

          {/* Exclude region canvas (active) */}
          {selectedMode === 'color' && color.excludeMode && !color.colorResultUrl && (
            <canvas
              ref={color.excludeCanvasRef}
              className="absolute top-0 left-0 w-full h-full cursor-crosshair"
              onMouseDown={color.handleExcludeMouseDown}
              onMouseMove={color.handleExcludeMouseMove}
              onMouseUp={color.handleExcludeMouseUp}
              onMouseLeave={color.handleExcludeMouseUp}
            />
          )}

          {/* Exclude region display (inactive) */}
          {selectedMode === 'color' && !color.excludeMode && !color.colorResultUrl && color.excludeRegions.length > 0 && (
            <canvas ref={color.excludeCanvasRef} className="absolute top-0 left-0 w-full h-full pointer-events-none" />
          )}

          {/* Region selection canvas */}
          {!erase.resultImageUrl && selectedMode === 'erase' && (
            <canvas
              ref={canvasRef}
              className="absolute top-0 left-0 w-full h-full cursor-crosshair"
              onMouseDown={(e) => erase.handleMouseDown(e, selectedMode, upload.previewUrl)}
              onMouseMove={erase.handleMouseMove}
              onMouseUp={erase.handleMouseUp}
              onMouseLeave={erase.handleMouseUp}
            />
          )}

          {/* Uploading */}
          {upload.isUploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="flex items-center gap-2 text-white"><Loader2 className="w-5 h-5 animate-spin" /><span>Uploading...</span></div>
            </div>
          )}

          {/* Processing */}
          {isProcessing && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2 text-white">
                <Loader2 className="w-8 h-8 animate-spin" />
                <span>{selectedMode === 'color' ? 'Changing colors...' : 'AI is erasing the region...'}</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-bg-200/50 flex items-center justify-center">
            <Image className="w-8 h-8 text-text-400" />
          </div>
          <div>
            <p className="text-text-200 font-medium">Upload a chart image</p>
            <p className="text-sm text-text-400 mt-1">Click or drag a file here</p>
          </div>
        </div>
      )}
    </div>
  );
}

function ColorControls({ color }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => { color.setIsEyedropping(!color.isEyedropping); color.setExcludeMode(false); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            color.isEyedropping ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/30' : 'bg-bg-200/50 text-text-300 hover:bg-bg-300/50 border border-bg-300/50'
          }`}
        >
          <Pipette className="w-4 h-4" /><span>Pick Color</span>
        </button>
        <button
          onClick={() => { color.setExcludeMode(!color.excludeMode); color.setIsEyedropping(false); }}
          className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
            color.excludeMode ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30' : 'bg-bg-200/50 text-text-300 hover:bg-bg-300/50 border border-bg-300/50'
          }`}
        >
          <Eraser className="w-3.5 h-3.5" /><span>Exclude Region</span>
        </button>
        <span className="text-xs text-text-500">
          {color.excludeMode ? 'Drag to select a region to exclude' : 'Click the color you want to change in the image'}
        </span>
      </div>

      {/* Exclude region list */}
      {color.excludeRegions.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-text-500">Excluded regions:</span>
          {color.excludeRegions.map((_, index) => (
            <button key={index} onClick={() => color.setExcludeRegions(prev => prev.filter((_, i) => i !== index))}
              className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/15 text-blue-400 rounded-lg text-xs hover:bg-blue-500/25 transition-colors">
              <span>{index + 1}</span><X className="w-3 h-3" />
            </button>
          ))}
          <button onClick={() => color.setExcludeRegions([])}
            className="flex items-center gap-1 px-2 py-0.5 bg-bg-300/50 text-text-500 rounded-lg text-xs hover:bg-bg-300 transition-colors">
            <RotateCcw className="w-3 h-3" /><span>Reset</span>
          </button>
        </div>
      )}

      {/* Color mapping list */}
      {color.colorMappings.length > 0 && (
        <div className="space-y-2">
          <span className="text-sm text-text-400 font-medium">Colors to change</span>
          {color.colorMappings.map((mapping, index) => (
            <div key={index} className="p-3 bg-bg-200/40 rounded-xl border border-bg-300/30 space-y-2">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg border-2 border-bg-300/50 shadow-inner" style={{ backgroundColor: mapping.original }} />
                  <span className="text-xs text-text-400 font-mono">{mapping.original}</span>
                </div>
                <span className="text-text-500">→</span>
                <div className="flex items-center gap-2">
                  <label className="relative cursor-pointer">
                    <div className="w-8 h-8 rounded-lg border-2 border-bg-300/50 shadow-inner" style={{ backgroundColor: mapping.replacement }} />
                    <input type="color" value={mapping.replacement} onChange={(e) => color.updateColorMapping(index, 'replacement', e.target.value.toUpperCase())} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
                  </label>
                  <span className="text-xs text-text-400 font-mono">{mapping.replacement}</span>
                </div>
                <div className="flex-1" />
                <button onClick={() => color.removeColorMapping(index)} className="p-1.5 text-text-500 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <details className="group">
                <summary className="text-xs text-text-500 cursor-pointer select-none hover:text-text-300 transition-colors">Also change similar colors</summary>
                <div className="flex items-center gap-2 mt-1.5">
                  <input type="range" min="5" max="80" value={mapping.tolerance} onChange={(e) => color.updateColorMapping(index, 'tolerance', parseInt(e.target.value))} className="flex-1 h-1.5 accent-violet-500" />
                  <span className="text-xs text-text-400 w-6 text-right">{mapping.tolerance}</span>
                </div>
              </details>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultBanner({ text, sub }) {
  return (
    <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-xl">
      <div className="flex items-center gap-2 text-green-400">
        <Check className="w-5 h-5" /><span className="font-medium">{text}</span>
      </div>
      <p className="text-sm text-text-400 mt-1">{sub}</p>
    </div>
  );
}

function RegionList({ regions, onRemove, onClear }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm text-text-400">Selected regions:</span>
      {regions.map((_, index) => (
        <button key={index} onClick={() => onRemove(index)}
          className="flex items-center gap-1 px-2 py-1 bg-rose-500/20 text-rose-400 rounded-lg text-sm hover:bg-rose-500/30 transition-colors">
          <span>Region {index + 1}</span><X className="w-3 h-3" />
        </button>
      ))}
      <button onClick={onClear} className="flex items-center gap-1 px-2 py-1 bg-bg-300/50 text-text-400 rounded-lg text-sm hover:bg-bg-300 transition-colors">
        <RotateCcw className="w-3 h-3" /><span>Reset</span>
      </button>
    </div>
  );
}


function Footer({ selectedMode, upload, color, erase, resetAll, isProcessing, onSendToChat, onClose }) {
  const handleSendToChat = async (resultUrl, mode) => {
    if (!onSendToChat) return;
    try {
      const res = await fetch(resultUrl);
      const blob = await res.blob();
      const file = new File([blob], `chart_${mode}_result.png`, { type: 'image/png' });
      const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(blob);
      });
      onSendToChat({ dataUrl, fileName: file.name, type: 'image' });
      if (onClose) onClose();
    } catch (e) {
      console.error('Send to chat error:', e);
    }
  };

  return (
    <div className="flex gap-3 px-6 py-4 border-t border-bg-300/30 bg-bg-200/20">
      {selectedMode === 'color' && color.colorResultUrl ? (
        <>
          <button onClick={resetAll} className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-text-300 hover:text-text-100 hover:bg-bg-300/50 rounded-xl border border-text-300/25 transition-colors">
            <Image className="w-4 h-4" /><span>Different Image</span>
          </button>
          <button onClick={color.clearColorResult} className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-text-300 hover:text-text-100 hover:bg-bg-300/50 rounded-xl border border-text-300/25 transition-colors">
            <RotateCcw className="w-4 h-4" /><span>Change Again</span>
          </button>
          <button onClick={color.handleDownloadColorResult} className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium bg-green-500 text-white hover:bg-green-600 rounded-xl transition-all">
            <Download className="w-4 h-4" /><span>Download</span>
          </button>
          {onSendToChat && (
            <button onClick={() => handleSendToChat(color.colorResultUrl, 'color')} className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 rounded-xl transition-all">
              <Check className="w-4 h-4" /><span>Send to Chat</span>
            </button>
          )}
        </>
      ) : selectedMode === 'color' ? (
        <>
          <button
            onClick={() => { upload.resetUpload(); color.resetColor(); }}
            disabled={!upload.previewUrl}
            className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-text-300 hover:text-text-100 hover:bg-bg-300/50 rounded-xl border border-text-300/25 transition-colors disabled:opacity-50"
          >
            <Image className="w-4 h-4" /><span>Different Image</span>
          </button>
          <button
            onClick={color.handleColorReplace}
            disabled={!upload.previewUrl || color.colorMappings.length === 0 || isProcessing}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium rounded-xl transition-all ${
              upload.previewUrl && color.colorMappings.length > 0 && !isProcessing
                ? 'bg-violet-500 text-white hover:bg-violet-600'
                : 'bg-bg-300/50 text-text-500 cursor-not-allowed'
            }`}
          >
            {isProcessing
              ? (<><Loader2 className="w-4 h-4 animate-spin" /><span>Processing...</span></>)
              : (<><Palette className="w-4 h-4" /><span>Change Color</span></>)
            }
          </button>
        </>
      ) : erase.resultImageUrl ? (
        <>
          <button onClick={resetAll} className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-text-300 hover:text-text-100 hover:bg-bg-300/50 rounded-xl border border-text-300/25 transition-colors">
            <Image className="w-4 h-4" /><span>Different Image</span>
          </button>
          <button onClick={erase.clearAllRegions} className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-text-300 hover:text-text-100 hover:bg-bg-300/50 rounded-xl border border-text-300/25 transition-colors">
            <RotateCcw className="w-4 h-4" /><span>Edit Again</span>
          </button>
          <button onClick={erase.handleDownload} className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium bg-green-500 text-white hover:bg-green-600 rounded-xl transition-all">
            <Download className="w-4 h-4" /><span>Download</span>
          </button>
          {onSendToChat && (
            <button onClick={() => handleSendToChat(erase.resultImageUrl, 'erase')} className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 rounded-xl transition-all">
              <Check className="w-4 h-4" /><span>Send to Chat</span>
            </button>
          )}
        </>
      ) : (
        <>
          <button
            onClick={() => { upload.resetUpload(); erase.resetErase(); }}
            disabled={!upload.previewUrl}
            className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-text-300 hover:text-text-100 hover:bg-bg-300/50 rounded-xl border border-text-300/25 transition-colors disabled:opacity-50"
          >
            <Image className="w-4 h-4" /><span>Different Image</span>
          </button>
          <button
            onClick={() => erase.handleInpaint(upload.s3ImageUrl)}
            disabled={!upload.s3ImageUrl || erase.selectedRegions.length === 0 || isProcessing || selectedMode !== 'erase'}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium rounded-xl transition-all ${
              upload.s3ImageUrl && erase.selectedRegions.length > 0 && !isProcessing && selectedMode === 'erase'
                ? 'bg-rose-500 text-white hover:bg-rose-600'
                : 'bg-bg-300/50 text-text-500 cursor-not-allowed'
            }`}
          >
            {isProcessing
              ? (<><Loader2 className="w-4 h-4 animate-spin" /><span>Processing...</span></>)
              : (<><Eraser className="w-4 h-4" /><span>Erase Region</span></>)
            }
          </button>
        </>
      )}
    </div>
  );
}


function FadeBadge({ show, color: badgeColor, icon, text }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setVisible(true);
      const timer = setTimeout(() => setVisible(false), 10000);
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [show]);

  const bgClass = badgeColor === 'violet' ? 'bg-violet-500/90' : 'bg-blue-500/90';

  return (
    <AnimatePresence>
      {show && visible && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3 }}
          className={`absolute bottom-3 left-3 flex items-center gap-2 px-3 py-1.5 ${bgClass} text-white text-sm rounded-full shadow-lg pointer-events-none`}
        >
          {icon}
          <span>{text}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
